import { useState, useRef, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Paperclip, Smile, Send, X, FileText, UserPlus } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';

const MessageInput = memo(({
  onSendMessage,
  onFilesSelected,
  selectedFiles = [],
  onRemoveFile,
  uploadingFiles = false,
  sending = false,
  conversationMembers = [],
  allCompanyMembers = [],
  onAddMemberToConversation = null
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [pendingMention, setPendingMention] = useState(null);
  const [mentionMap, setMentionMap] = useState({}); // Maps display names to user IDs
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const handleSend = () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || sending || uploadingFiles) {
      return;
    }

    // Convert display mentions to UUID format for sending
    let messageToSend = newMessage.trim();
    Object.entries(mentionMap).forEach(([displayName, userId]) => {
      // Replace @Name with @Name[uuid]
      const displayMention = `@${displayName}`;
      const fullMention = `@${displayName}[${userId}]`;
      // Use split and join for safe string replacement
      messageToSend = messageToSend.split(displayMention).join(fullMention);
    });

    onSendMessage(messageToSend);

    // Clear the contentEditable div
    if (messageInputRef.current) {
      messageInputRef.current.innerHTML = '';
    }
    setNewMessage('');
    setMentionMap({});
    setShowEmojiPicker(false);
    setShowMentionDropdown(false);
  };

  const handleEmojiClick = (emojiObject) => {
    const element = messageInputRef.current;
    if (!element) return;

    // Insert emoji at cursor position
    element.focus();
    const emoji = emojiObject.emoji;
    document.execCommand('insertText', false, emoji);
    setNewMessage(element.textContent || '');
  };

  const handleMessageChange = (e) => {
    const element = e.target;

    // Get text content while preserving mention markers
    let text = '';
    element.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-mention')) {
        // Preserve the mention in the text
        const displayName = node.textContent.substring(1); // Remove @ symbol
        text += `@${displayName}`;
      } else {
        text += node.textContent;
      }
    });

    setNewMessage(text);

    // Get cursor position - need to calculate absolute position in contentEditable
    const selection = window.getSelection();
    const range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;

    let cursorPos = 0;
    if (range) {
      // Calculate the absolute cursor position by walking through all nodes
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        null
      );

      let currentNode;
      while ((currentNode = walker.nextNode())) {
        if (currentNode === range.startContainer) {
          cursorPos += range.startOffset;
          break;
        }

        if (currentNode.nodeType === Node.TEXT_NODE) {
          cursorPos += currentNode.textContent.length;
        } else if (currentNode.nodeType === Node.ELEMENT_NODE && currentNode.hasAttribute('data-mention')) {
          cursorPos += currentNode.textContent.length;
        }
      }
    } else {
      cursorPos = text.length;
    }

    setMentionCursorPosition(cursorPos);

    // Check if typing @ for mentions
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      // Only show dropdown if there's no space after @ and we're within 20 chars
      // Don't show mentions in DMs (allCompanyMembers will be empty)
      if (!textAfterAt.includes(' ') && textAfterAt.length <= 20 && allCompanyMembers.length > 0) {
        setMentionSearch(textAfterAt.toLowerCase());
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleSelectMention = async (member) => {
    // AI Assistant doesn't need to be added to chat - always proceed with mention
    if (member.id === 'ai-assistant') {
      insertMention(member);
      return;
    }

    // Check if member is in the conversation
    const isMemberInConversation = conversationMembers.some(m => m.id === member.id);

    if (!isMemberInConversation && onAddMemberToConversation) {
      // Store the pending mention and show confirmation dialog
      setPendingMention(member);
      setShowAddMemberDialog(true);
      setShowMentionDropdown(false);
      return;
    }

    // Member is in conversation, proceed with mention
    insertMention(member);
  };

  const insertMention = (member) => {
    const element = messageInputRef.current;
    if (!element) return;

    // Get the current selection/cursor position
    const selection = window.getSelection();
    const range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range) return;

    // Find the text node containing the @ symbol
    let textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) {
      textNode = textNode.childNodes[range.startOffset];
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
    }

    const textContent = textNode.textContent || '';
    const cursorOffset = range.startOffset;
    const textBeforeCursor = textContent.substring(0, cursorOffset);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const displayName = `${member.first_name} ${member.last_name}`;

      // Store the mapping of display name to user ID
      setMentionMap(prev => ({
        ...prev,
        [displayName]: member.id
      }));

      // Split the text node at the @ symbol
      const beforeAt = textContent.substring(0, lastAtSymbol);
      const afterCursor = textContent.substring(cursorOffset);

      // Create the mention span element
      const mentionSpan = document.createElement('span');
      mentionSpan.className = 'font-bold text-blue-600 cursor-pointer';
      mentionSpan.contentEditable = 'false';
      mentionSpan.setAttribute('data-mention', member.id);
      mentionSpan.textContent = `@${displayName}`;

      // Create text nodes for before and after
      const beforeNode = document.createTextNode(beforeAt);
      const spaceNode = document.createTextNode(' ');
      const afterNode = document.createTextNode(afterCursor);

      // Replace the current text node with the new structure
      const parent = textNode.parentNode;
      parent.insertBefore(beforeNode, textNode);
      parent.insertBefore(mentionSpan, textNode);
      parent.insertBefore(spaceNode, textNode);
      parent.insertBefore(afterNode, textNode);
      parent.removeChild(textNode);

      // Update the message state
      let newText = '';
      element.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          newText += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-mention')) {
          const displayName = node.textContent.substring(1);
          newText += `@${displayName}`;
        } else {
          newText += node.textContent;
        }
      });
      setNewMessage(newText);

      setShowMentionDropdown(false);
      setMentionSearch('');

      // Set cursor position after the mention
      setTimeout(() => {
        element.focus();
        const newRange = document.createRange();
        const newSel = window.getSelection();
        newRange.setStart(spaceNode, 1);
        newRange.collapse(true);
        newSel?.removeAllRanges();
        newSel?.addRange(newRange);
      }, 0);
    }
  };

  const handleConfirmAddMember = async () => {
    if (!pendingMention || !onAddMemberToConversation) return;

    try {
      // Add member to conversation
      await onAddMemberToConversation(pendingMention.id);

      // Insert the mention
      insertMention(pendingMention);

      // Clean up
      setPendingMention(null);
      setShowAddMemberDialog(false);
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Failed to add member to chat');
    }
  };

  const handleCancelAddMember = () => {
    setPendingMention(null);
    setShowAddMemberDialog(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onFilesSelected(files);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };

  return (
    <div className="border-t bg-background relative flex-shrink-0">
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
      {/* File Preview */}
      {selectedFiles.length > 0 && (
        <div className="border-b p-3">
          <div className="flex items-center gap-2 flex-wrap">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative group">
                {file.type.startsWith('image/') ? (
                  <div className="relative">
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={file.name}
                      className="h-20 w-20 object-cover rounded-lg border"
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => onRemoveFile(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative flex items-center gap-2 p-2 bg-muted rounded-lg border">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div className="max-w-[150px]">
                      <p className="text-sm truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 ml-2"
                      onClick={() => onRemoveFile(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Emoji Picker Popover */}
      {showEmojiPicker && (
        <div className="absolute bottom-full right-4 mb-2 z-50">
          <div className="relative">
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme="auto"
              height={400}
              width={350}
              searchPlaceHolder="Search emoji..."
              previewConfig={{
                showPreview: false
              }}
            />
            {/* Click outside to close */}
            <div 
              className="fixed inset-0 z-[-1]" 
              onClick={() => setShowEmojiPicker(false)}
            />
          </div>
        </div>
      )}
      
      <div className="p-4">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button 
            size="icon" 
            variant="ghost" 
            title="Attach file"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFiles}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <div className="flex-1 relative">
            <div
              ref={messageInputRef}
              contentEditable
              onInput={handleMessageChange}
              onKeyDown={(e) => {
                // If mention dropdown is open and Enter is pressed, select first option
                if (e.key === 'Enter' && !e.shiftKey && showMentionDropdown) {
                  e.preventDefault();

                  // Check if AI Assistant should be shown (always first)
                  const aiAssistantMatch = 'ai assistant'.includes(mentionSearch.toLowerCase()) || mentionSearch === '';

                  if (aiAssistantMatch) {
                    // Select AI Assistant
                    handleSelectMention({
                      id: 'ai-assistant',
                      first_name: 'AI',
                      last_name: 'Assistant',
                      avatar_url: null,
                      email: 'AI Assistant'
                    });
                  } else {
                    // Select first filtered member
                    const filteredMembers = allCompanyMembers.filter(member => {
                      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
                      return fullName.includes(mentionSearch);
                    });
                    if (filteredMembers.length > 0) {
                      handleSelectMention(filteredMembers[0]);
                    }
                  }
                  return;
                }
                // If dropdown is not open, send message on Enter
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
                // Close mention dropdown on Escape
                if (e.key === 'Escape' && showMentionDropdown) {
                  e.preventDefault();
                  setShowMentionDropdown(false);
                }
              }}
              className="flex-1 min-h-[40px] max-h-[120px] overflow-y-auto px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              data-placeholder={allCompanyMembers.length > 0 ? "Type a message... (Type @ to mention someone)" : "Type a message..."}
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
            />

            {/* Mention Dropdown */}
            {showMentionDropdown && (
              <div className="absolute bottom-full left-0 mb-2 w-full max-w-sm bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                {/* AI Assistant - Always first */}
                {('ai assistant'.includes(mentionSearch.toLowerCase()) || mentionSearch === '') && (
                  <button
                    onClick={() => handleSelectMention({
                      id: 'ai-assistant',
                      first_name: 'AI',
                      last_name: 'Assistant',
                      avatar_url: null,
                      email: 'AI Assistant'
                    })}
                    className="w-full flex items-center space-x-3 p-2 hover:bg-blue-50 transition-colors text-left border-b"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                      AI
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">
                          AI Assistant
                        </p>
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">Bot</Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">Ask me anything</p>
                    </div>
                  </button>
                )}

                {/* Company Members */}
                {allCompanyMembers.length > 0 && allCompanyMembers
                  .filter(member => {
                    const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
                    return mentionSearch === '' || fullName.includes(mentionSearch);
                  })
                  .slice(0, 8)
                  .map(member => {
                    const isInConversation = conversationMembers.some(m => m.id === member.id);
                    return (
                      <button
                        key={member.id}
                        onClick={() => handleSelectMention(member)}
                        className="w-full flex items-center space-x-3 p-2 hover:bg-gray-100 transition-colors text-left"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.avatar_url} alt={`${member.first_name} ${member.last_name}`} />
                          <AvatarFallback>
                            {`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">
                              {member.first_name} {member.last_name}
                            </p>
                            {!isInConversation && (
                              <Badge variant="outline" className="text-xs">Not in chat</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{member.email}</p>
                        </div>
                      </button>
                    );
                  })}

                {allCompanyMembers.length === 0 && mentionSearch && !('ai assistant'.includes(mentionSearch.toLowerCase())) && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No members found
                  </div>
                )}
              </div>
            )}
          </div>
          <Button 
            size="icon" 
            variant="ghost"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className={showEmojiPicker ? 'bg-accent' : ''}
            title="Add emoji"
          >
            <Smile className="h-4 w-4" />
          </Button>
          <Button 
            size="icon" 
            onClick={handleSend}
            disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending || uploadingFiles}
            title="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Add Member Confirmation Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add member to chat?</DialogTitle>
            <DialogDescription>
              {pendingMention && (
                <div className="mt-4">
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={pendingMention.avatar_url} />
                      <AvatarFallback>
                        {`${pendingMention.first_name?.[0] || ''}${pendingMention.last_name?.[0] || ''}`}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">
                        {pendingMention.first_name} {pendingMention.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{pendingMention.email}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm">
                    This person is not in the chat. Would you like to add them before mentioning them?
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelAddMember}>
              Cancel
            </Button>
            <Button onClick={handleConfirmAddMember}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add to Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput;